import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  IconButton,
  Select,
  Stack,
  Text,
  useTheme,
} from '@ltvco/refresh-lib/theme';
import { InternationalPhoneInput } from '@ltvco/refresh-lib/v2';
import {
  Controller,
  useForm,
  yupResolver,
  Yup as yup,
} from '@ltvco/refresh-lib/vendors';
import { Close, ExpandMore, Lock } from '@mui/icons-material';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type ComponentType,
} from 'react';

import imgModalSetUp from 'images/img_modal_set_up.svg';
import imgPhoneModal from 'images/img_phone_modal.svg';

import {
  SIGNUP_REASON_OPTIONS,
  ACCOUNT_SETUP_MODAL_COPY,
  ACCOUNT_SETUP_SUCCESS_COPY,
  type AccountSetupMode,
  type SignupReasonCode,
} from './constants';
import { useAccountSetup } from './useAccountSetup';

type AccountSetupPhoneInputProps = ComponentProps<
  typeof InternationalPhoneInput
> & {
  onDialCodeChange?: (dialCode: string) => void;
};

const AccountSetupPhoneInput =
  InternationalPhoneInput as ComponentType<AccountSetupPhoneInputProps>;

interface AccountSetupFormValues {
  phoneNumber: string;
  signupReasonCode: string;
}

function buildAccountSetupSchema(mode: AccountSetupMode) {
  const showPhoneField = mode === 'both' || mode === 'phone';
  const showSignupReasonField = mode === 'both' || mode === 'signupReason';

  return yup.object({
    phoneNumber: showPhoneField
      ? yup.string().required('Please enter a valid phone number.')
      : yup.string(),
    signupReasonCode: showSignupReasonField
      ? yup.string().required('Please select a reason.')
      : yup.string(),
  });
}

export interface AccountSetupModalValues {
  mode: AccountSetupMode;
  phoneNumber?: string;
  countryDialCode?: string;
  signupReasonCode?: SignupReasonCode;
}

export interface AccountSetupModalProps {
  open: boolean;
  mode: AccountSetupMode;
  onClose: () => void;
  onSuccess?: () => void;
}

export function AccountSetupModal({
  open,
  mode,
  onClose,
  onSuccess,
}: AccountSetupModalProps) {
  const theme = useTheme();
  const { mutate, isPending, error, reset: resetMutation } = useAccountSetup();
  const [isSuccess, setIsSuccess] = useState(false);
  const [resetSignal, setResetSignal] = useState(false);
  const countryDialCodeRef = useRef('1');

  const schema = useMemo(() => buildAccountSetupSchema(mode), [mode]);

  const {
    register,
    handleSubmit,
    reset: resetForm,
    control,
    formState: { errors, isValid, touchedFields },
  } = useForm<AccountSetupFormValues>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      phoneNumber: '',
      signupReasonCode: '',
    },
  });

  const showPhoneField = mode === 'both' || mode === 'phone';
  const showSignupReasonField = mode === 'both' || mode === 'signupReason';
  const { title, subtitle } = ACCOUNT_SETUP_MODAL_COPY[mode];
  const successCopy = ACCOUNT_SETUP_SUCCESS_COPY[mode];

  const dialogPaperSx = useMemo(
    () => ({
      overflow: 'visible',
      borderRadius: theme.spacing(1.5),
    }),
    [theme]
  );

  const selectFieldSx = useMemo(
    () => ({
      borderRadius: theme.spacing(1.25),
      height: theme.spacing(6.25),
      '& .MuiOutlinedInput-notchedOutline legend': {
        display: 'none',
      },
    }),
    [theme]
  );

  const pillButtonSx = useMemo(
    () => ({
      borderRadius: theme.spacing(12.5),
      px: 3,
    }),
    [theme]
  );

  const signupReasonSelectOptions = useMemo(
    () =>
      SIGNUP_REASON_OPTIONS.map(({ code, label }) => ({
        value: String(code),
        display: label,
      })),
    []
  );

  const illustrationSize = theme.spacing(15);

  useEffect(() => {
    if (!open) {
      if (isSuccess) {
        return;
      }

      resetForm();
      countryDialCodeRef.current = '1';
      setResetSignal(true);
      setIsSuccess(false);
      resetMutation();
    }
  }, [open, resetForm, resetMutation, isSuccess]);

  const handleClose = () => {
    if (isPending) {
      return;
    }
    onClose();
  };

  const handleDone = () => {
    if (isPending) {
      return;
    }
    setIsSuccess(false);
    onSuccess?.();
    onClose();
  };

  const onSubmit = (values: AccountSetupFormValues) => {
    if (isPending) {
      return;
    }

    const payload: AccountSetupModalValues = { mode };

    if (showPhoneField) {
      payload.phoneNumber = values.phoneNumber;
      payload.countryDialCode = countryDialCodeRef.current;
    }

    if (showSignupReasonField && values.signupReasonCode !== '') {
      payload.signupReasonCode = Number(
        values.signupReasonCode
      ) as SignupReasonCode;
    }

    mutate(payload, {
      onSuccess: () => {
        setIsSuccess(true);
      },
      onError: (submissionError) => {
        console.error('Account setup submission failed:', submissionError);
      },
    });
  };

  if (isSuccess) {
    return (
      <Dialog
        open
        onClose={handleDone}
        maxWidth="sm"
        fullWidth
        aria-labelledby="account-setup-success-title"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <Box sx={{ position: 'relative', pt: 4, px: 3 }}>
          <IconButton
            aria-label="Close"
            onClick={handleDone}
            size="small"
            sx={{ position: 'absolute', top: 1.5, right: 1.5 }}
          >
            <Close />
          </IconButton>
          <Stack alignItems="center" spacing={1.5} textAlign="center" pb={1}>
            <img
              src={imgModalSetUp}
              alt=""
              width={illustrationSize}
              height={illustrationSize}
            />
            <Text id="account-setup-success-title" variant="h4" component="h2">
              {successCopy.title}
            </Text>
            <Text variant="body2" color="text.secondary">
              {successCopy.subtitle}
            </Text>
          </Stack>
        </Box>

        <Divider sx={{ mt: 2 }} />

        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={handleDone} sx={pillButtonSx}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isPending}
      aria-labelledby="account-setup-modal-title"
      PaperProps={{ sx: dialogPaperSx }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ position: 'relative', pt: 3, px: 3 }}>
          <Stack alignItems="center" spacing={1.5} textAlign="center">
            <img
              src={imgPhoneModal}
              alt="Phone Image"
              width={illustrationSize}
              height={illustrationSize}
            />
            <Text id="account-setup-modal-title" variant="h1">
              {title}
            </Text>
            <Text variant="body1" color="text.secondary">
              {subtitle}
            </Text>
          </Stack>
        </Box>

        <DialogContent sx={{ px: 3, pt: 2.5, pb: 0, overflow: 'visible' }}>
          <Stack spacing={2.5}>
            {error && (
              <Alert severity="error">
                {error.message || 'Something went wrong. Please try again.'}
              </Alert>
            )}
            {showPhoneField && (
              <Stack spacing={1}>
                <Text variant="body1" fontWeight={700}>
                  Phone number
                </Text>
                <Controller
                  control={control}
                  name="phoneNumber"
                  render={({ field, fieldState }) => (
                    <AccountSetupPhoneInput
                      field={field}
                      name="phoneNumber"
                      error={
                        fieldState.isTouched && Boolean(errors.phoneNumber)
                      }
                      theme={theme}
                      resetSignal={resetSignal}
                      setResetSignal={setResetSignal}
                      onDialCodeChange={(dialCode: string) => {
                        countryDialCodeRef.current = dialCode;
                      }}
                    />
                  )}
                />
                {touchedFields.phoneNumber && errors.phoneNumber?.message && (
                  <Text variant="caption" color="error">
                    {errors.phoneNumber.message}
                  </Text>
                )}
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Lock sx={{ fontSize: 'small', color: 'text.secondary' }} />
                  <Text variant="caption" color="text.secondary">
                    We&apos;ll never share your number. Standard message rates
                    may apply.
                  </Text>
                </Stack>
              </Stack>
            )}

            {showSignupReasonField && (
              <Stack spacing={1}>
                <Text variant="body1">Reason for signing up</Text>
                <FormControl fullWidth>
                  <Select
                    {...register('signupReasonCode')}
                    id="signup-reason"
                    options={signupReasonSelectOptions}
                    placeholder="Select a reason"
                    fullWidth
                    disabled={isPending}
                    IconComponent={ExpandMore}
                    sx={selectFieldSx}
                  />
                </FormControl>
                <Text variant="caption" color="text.secondary">
                  This stays private and just helps us tailor your results.
                </Text>
              </Stack>
            )}
          </Stack>
        </DialogContent>

        <Divider sx={{ mt: 3 }} />

        <DialogActions
          sx={{ px: 3, py: 2, justifyContent: 'flex-end', gap: 1 }}
        >
          <Button
            type="submit"
            variant="contained"
            disabled={!isValid || isPending}
            startIcon={
              isPending ? (
                <CircularProgress
                  size={parseFloat(theme.spacing(2))}
                  color="inherit"
                />
              ) : null
            }
            sx={pillButtonSx}
          >
            {isPending ? 'Saving...' : 'Save & continue'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
